import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useNotesAuth } from '@/lib/notesAuth';
import { notesApi } from '@/lib/notesApi';
import { toast } from 'sonner';
import { ModuleSwitch } from './ModuleSwitch';
import DailyPlannerView from './DailyPlannerView';
import WeeklyPlannerView from './WeeklyPlannerView';
import MonthlyPlannerView from './MonthlyPlannerView';
import { EventDialog, MeetingDialog, GoalDialog } from './PlannerModals';
import PlannerReminderAlerts from './PlannerReminderAlerts';
import PlannerNavShortcuts from './PlannerNavShortcuts';
import { Skeleton } from '@/components/ui/skeleton';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import PlannerCommandPalette from './PlannerCommandPalette';
import PlannerAutoRefresh from './PlannerAutoRefresh';
import {
  ChevronLeft, ChevronRight, ChevronDown, CalendarRange, Sun, Moon, LogOut, Plus,
  CalendarDays, CalendarClock, CalendarCheck2, CalendarPlus, Users, Target, Search,
} from 'lucide-react';
import {
  todayStr, addDays, addWeeks, addMonths, weekStart, prettyDateLong,
  weekRangeLabel, monthLabel, tzOffsetMin, parseISO, fmtISO,
} from './plannerUtils';
import { setViewerId, SmartPopover, TaskQuickActions } from './TaskCardKit';
import TaskSubtasks from './TaskSubtasks';
import ChatDock from './ChatDock';
import { useChatEnabled } from './useChatEnabled';
import { ListChecks, X, MessageSquare } from 'lucide-react';

const VIEWS = [
  { key: 'daily', label: 'Daily', icon: CalendarClock },
  { key: 'weekly', label: 'Weekly', icon: CalendarDays },
  { key: 'monthly', label: 'Monthly', icon: CalendarRange },
];

/* Immutably flip the `completed` flag on the matching goal anywhere inside the
   feed data tree (daily goals list, weekly/monthly goal rows, etc.) so a goal
   toggle shows instantly without waiting for the network. Only touches objects
   that carry BOTH the goal id and a boolean `completed` — so tasks/events (which
   use different ids / lack `completed`) are never affected. A silent background
   reload afterwards reconciles any derived stats (habit %, streaks, badges). */
function flipGoalCompleted(node, id, periodKey) {
  if (Array.isArray(node)) {
    let changed = false;
    const next = node.map((item) => {
      const r = flipGoalCompleted(item, id, periodKey);
      if (r !== item) changed = true;
      return r;
    });
    return changed ? next : node;
  }
  if (node && typeof node === 'object') {
    if (node.id === id && typeof node.completed === 'boolean'
      && (periodKey == null || node.period_key == null || node.period_key === periodKey)) {
      return { ...node, completed: !node.completed };
    }
    let changed = false;
    const next = {};
    for (const k of Object.keys(node)) {
      const r = flipGoalCompleted(node[k], id, periodKey);
      if (r !== node[k]) changed = true;
      next[k] = r;
    }
    return changed ? next : node;
  }
  return node;
}

const REFRESH_SECS = 180;

/* Build a lightweight "digest" of the currently-visible feed so a silent
   auto-refresh can detect NEW remote activity (shared events/meetings that
   appeared, shared tasks that got completed by a teammate). Returns null for
   the monthly view (which only carries per-day counts, no item ids). */
function collectDigest(data, view) {
  if (!data || view === 'monthly') return null;
  const events = new Map();   // id → isSharedWithMe (I'm not the owner)
  const meetings = new Map(); // id → isSharedWithMe (I'm an attendee, not owner)
  const tasksDone = new Map(); // id → { done, shared }
  const addEvent = (e) => { if (e && e.id != null) events.set(e.id, e.is_owner === false); };
  const addMeeting = (m) => { if (m && m.id != null) meetings.set(m.id, m.is_owner === false); };
  const addTask = (t) => {
    if (!t || t.id == null) return;
    const done = !!(t.is_completed != null ? t.is_completed : t.completed);
    const shared = !!(t.is_shared || t.shared_by);
    tasksDone.set(t.id, { done, shared });
  };
  if (view === 'daily') {
    (data.events || []).forEach(addEvent);
    (data.all_day_events || []).forEach(addEvent);
    (data.meetings || []).forEach(addMeeting);
    (data.tasks || []).forEach(addTask);
  } else if (view === 'weekly') {
    (data.days || []).forEach((d) => {
      (d.events || []).forEach(addEvent);
      (d.meetings || []).forEach(addMeeting);
      (d.tasks || []).forEach(addTask);
    });
    (data.all_day_events || []).forEach(addEvent);
  }
  return { events, meetings, tasksDone };
}

/* Diff two digests → a short human summary of NEW shared activity, or null. */
function diffDigest(prev, next) {
  if (!prev || !next) return null;
  let newEv = 0; let newMt = 0; let done = 0;
  next.events.forEach((shared, id) => { if (shared && !prev.events.has(id)) newEv += 1; });
  next.meetings.forEach((shared, id) => { if (shared && !prev.meetings.has(id)) newMt += 1; });
  next.tasksDone.forEach((rec, id) => {
    const p = prev.tasksDone.get(id);
    if (rec.shared && rec.done && p && !p.done) done += 1;
  });
  const bits = [];
  if (newEv) bits.push(`${newEv} new shared event${newEv > 1 ? 's' : ''}`);
  if (newMt) bits.push(`${newMt} new meeting${newMt > 1 ? 's' : ''}`);
  if (done) bits.push(`${done} shared task${done > 1 ? 's' : ''} completed`);
  return bits.length ? bits.join(' · ') : null;
}

export default function PlannerAppShell() {
  const { principal, logout, theme, toggleTheme } = useNotesAuth();
  const isDark = theme === 'dark';
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tz = tzOffsetMin();

  // Resolve deep-link (?view=&date=) ONCE, synchronously, so the very first
  // feed load uses the right view/date (avoids a stale double-fetch race).
  const initRef = useRef(null);
  if (initRef.current === null) {
    const v = searchParams.get('view');
    const d = searchParams.get('date');
    initRef.current = {
      v: ['daily', 'weekly', 'monthly'].includes(v) ? v : 'daily',
      d: /^\d{4}-\d{2}-\d{2}$/.test(d || '') ? d : todayStr(),
    };
  }

  const [view, setView] = useState(initRef.current.v);
  const [anchor, setAnchor] = useState(initRef.current.d);
  const [jumpOpen, setJumpOpen] = useState(false); // "Jump to date" (G) calendar popover
  const [paletteOpen, setPaletteOpen] = useState(false); // Ctrl/Cmd+K command palette
  const touchRef = useRef(null);                    // swipe-navigation gesture tracking
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [directory, setDirectory] = useState([]);
  const [taskDirectory, setTaskDirectory] = useState([]);
  const reqSeq = useRef(0);
  const prevDigestRef = useRef(null);
  const digestKeyRef = useRef(null);

  const [eventModal, setEventModal] = useState({ open: false, initial: null, date: null });
  const [meetingModal, setMeetingModal] = useState({ open: false, initial: null, date: null });
  const [goalModal, setGoalModal] = useState({ open: false, initial: null });
  // Full-parity task-card overlays (match the Tasks → Today grid behaviour).
  const [quickPanel, setQuickPanel] = useState(null);       // { task, x, y }
  const [subtaskPopover, setSubtaskPopover] = useState(null); // { id, anchor, canEdit }

  // Internal chat — same dock used by Notes & Tasks. The docked state lives in
  // localStorage ('chat_docked'), so a dock set in Notes/Tasks carries over to
  // the Planner (and vice-versa). Auto-open when already docked.
  const { enabled: chatEnabled, enabledRef: chatEnabledRef } = useChatEnabled();
  const [chatOpen, setChatOpen] = useState(() => {
    try { return localStorage.getItem('chat_docked') === '1'; } catch { return false; }
  });
  const [chatUnread, setChatUnread] = useState(0);
  const [chatReserve, setChatReserve] = useState(0); // right padding when chat is docked
  useEffect(() => { try { if (chatEnabled && new URLSearchParams(window.location.search).get('chat')) setChatOpen(true); } catch { /* noop */ } }, [chatEnabled]);
  useEffect(() => { if (!chatEnabled) { setChatOpen(false); setChatReserve(0); } }, [chatEnabled]);

  // Let the shared avatar components hide the viewer's own avatar.
  setViewerId(principal?.id || null);

  // Strip the deep-link params from the URL once (state already captured above).
  useEffect(() => {
    if (searchParams.get('view') || searchParams.get('date')) {
      const next = new URLSearchParams(searchParams);
      next.delete('view'); next.delete('date');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // React to the global "p then d/w/m" chord when already mounted on the planner
  // (URL params are stripped after first mount, so navigation alone can't re-drive
  // the view — this switches it in place).
  useEffect(() => {
    const onNav = (e) => {
      const v = e?.detail?.view;
      if (v === 'daily' || v === 'weekly' || v === 'monthly') setView(v);
    };
    window.addEventListener('planner:navigate', onNav);
    return () => window.removeEventListener('planner:navigate', onNav);
  }, []);

  const loadDirs = useCallback(() => {
    notesApi.get('/planner/directory').then(({ data }) => setDirectory(data.users || [])).catch(() => {});
    notesApi.get('/planner/tasks').then(({ data }) => setTaskDirectory(data.tasks || [])).catch(() => {});
  }, []);
  useEffect(() => { loadDirs(); }, [loadDirs]);

  const load = useCallback(async ({ silent = false, digest = false } = {}) => {
    const seq = ++reqSeq.current;
    if (!silent) setLoading(true);
    try {
      const { data } = await notesApi.get('/planner/feed', { params: { view, date: anchor, tz_offset: tz } });
      if (seq !== reqSeq.current) return;
      setData(data);
      // Auto/manual refresh: surface NEW shared activity against the last-seen
      // baseline. The baseline is refreshed on every load (so the user's own
      // actions never re-toast), but the toast only fires on a digest refresh.
      const key = `${view}:${anchor}`;
      const nextDigest = collectDigest(data, view);
      if (digest && silent && nextDigest && prevDigestRef.current && digestKeyRef.current === key) {
        const msg = diffDigest(prevDigestRef.current, nextDigest);
        if (msg) toast.message('Planner updated', { description: msg, id: 'planner-auto-refresh' });
      }
      prevDigestRef.current = nextDigest;
      digestKeyRef.current = key;
    } catch (e) {
      if (seq === reqSeq.current && !silent) toast.error('Could not load planner');
    } finally {
      if (seq === reqSeq.current && !silent) setLoading(false);
    }
  }, [view, anchor, tz]);

  useEffect(() => { load(); }, [load]);

  // Keyboard shortcuts:
  //   • "T" opens the Add-a-Task dialog (routes to Tasks with ?compose=1).
  //   • "C" opens chat (when enabled).
  // Keyboard shortcuts:
  //   • "Ctrl/Cmd + K" toggles the command palette (search + quick-create + date jump).
  //   • "T" opens the Add-a-Task dialog (routes to Tasks with ?compose=1).
  //   • "C" opens chat (when enabled).
  //   • "G" opens the "Jump to date" calendar.
  //   • "Home" snaps the current view back to today.
  //   • "←" / "→" step to the previous / next period — a DAY in Daily view, a
  //     WEEK in Weekly view, and a MONTH in Monthly view.
  // All (except the palette toggle) are ignored while typing in a field, when a
  // Planner modal / task overlay / the jump calendar / the palette is open, or
  // when a dropdown / select menu is open.
  useEffect(() => {
    const onKey = (e) => {
      // Command palette toggle works everywhere, even inside inputs.
      if ((e.ctrlKey || e.metaKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setPaletteOpen((v) => !v);
        return;
      }
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const el = document.activeElement;
      const tag = el && el.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (el && el.isContentEditable)) return;
      if (eventModal.open || meetingModal.open || goalModal.open || quickPanel || subtaskPopover || jumpOpen || paletteOpen) return;
      // Don't hijack arrows while an open menu / select / listbox is using them.
      if (document.querySelector('[role="menu"], [role="listbox"]')) return;
      if (e.key === 't' || e.key === 'T') {
        e.preventDefault();
        navigate('/notes/tasks?compose=1');
      } else if ((e.key === 'c' || e.key === 'C') && chatEnabledRef.current) {
        e.preventDefault();
        setChatOpen(true);
      } else if (e.key === 'g' || e.key === 'G') {
        e.preventDefault();
        setJumpOpen(true);
      } else if (e.key === 'Home') {
        e.preventDefault();
        setAnchor(todayStr());
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        e.preventDefault();
        const dir = e.key === 'ArrowLeft' ? -1 : 1;
        setAnchor((a) => (view === 'daily' ? addDays(a, dir) : view === 'weekly' ? addWeeks(a, dir) : addMonths(a, dir)));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate, view, eventModal.open, meetingModal.open, goalModal.open, quickPanel, subtaskPopover, jumpOpen, paletteOpen]);

  // ---- navigation ----
  const step = (dir) => {
    setAnchor((a) => (view === 'daily' ? addDays(a, dir) : view === 'weekly' ? addWeeks(a, dir) : addMonths(a, dir)));
  };
  const goToday = () => setAnchor(todayStr());

  // ---- touch swipe navigation (mobile / tablet) ----
  // Swipe LEFT → next period, swipe RIGHT → previous period. Horizontal intent
  // must clearly dominate so normal vertical scrolling is never hijacked.
  const onBodyTouchStart = (e) => {
    if (e.touches.length !== 1) { touchRef.current = null; return; }
    const t = e.touches[0];
    touchRef.current = { x: t.clientX, y: t.clientY, at: Date.now() };
  };
  const onBodyTouchEnd = (e) => {
    const s = touchRef.current; touchRef.current = null;
    if (!s) return;
    if (eventModal.open || meetingModal.open || goalModal.open || jumpOpen) return;
    const t = e.changedTouches && e.changedTouches[0];
    if (!t) return;
    const dx = t.clientX - s.x;
    const dy = t.clientY - s.y;
    if (Date.now() - s.at > 600) return;            // too slow to be a swipe
    if (Math.abs(dx) < 60) return;                   // too short
    if (Math.abs(dx) < Math.abs(dy) * 1.5) return;   // too vertical → let it scroll
    step(dx < 0 ? 1 : -1);
  };
  const isOnToday = view === 'monthly'
    ? anchor.slice(0, 7) === todayStr().slice(0, 7)
    : (view === 'weekly' ? weekStart(anchor) === weekStart(todayStr()) : anchor === todayStr());

  const headline = view === 'daily' ? prettyDateLong(anchor)
    : view === 'weekly' ? weekRangeLabel(weekStart(anchor))
      : monthLabel(anchor);
  const sub = view === 'daily' ? (anchor === todayStr() ? 'Today' : 'Day') : view === 'weekly' ? 'Week' : 'Month';

  // ---- item handlers ----
  const onToggleGoal = async (g, note) => {
    // Instant optimistic feedback for the checkbox on PRIVATE goals (GoalRow
    // passes `completed`); shared goals and habit-grid ticks rely on the silent
    // reload so the team roster / my-tick state stay accurate.
    if (g && !g.is_shared && typeof g.completed === 'boolean') {
      setData((d) => (d ? flipGoalCompleted(d, g.id, g.period_key) : d));
    }
    try {
      await notesApi.post(`/planner/goals/${g.id}/toggle`, {
        period_key: g.period_key,
        ...(note ? { note } : {}),
      });
      load({ silent: true });
    } catch (e) {
      toast.error('Could not update goal');
      load({ silent: true }); // revert to server truth, still no skeleton flash
    }
  };
  const onQuantityGoal = async (g, op, note) => {
    // Quantity logging (+/-) always relies on the silent reload so the team
    // totals / contributor avatars / logs stay accurate.
    try {
      await notesApi.post(`/planner/goals/${g.id}/toggle`, {
        period_key: g.period_key,
        op: op || 'inc',
        ...(note ? { note } : {}),
      });
      load({ silent: true });
    } catch (e) {
      toast.error('Could not update goal');
      load({ silent: true });
    }
  };
  const onOpenTask = (id) => navigate(`/notes/tasks?task=${id}`);
  const onOpenNote = (id) => navigate(`/notes?note=${id}`);
  const onOpenItem = (item) => {
    if (item.kind === 'meeting') {
      if (!item.is_owner) { toast.message('Meeting', { description: 'You are an attendee of this meeting.' }); return; }
      setMeetingModal({ open: true, initial: item, date: item.date });
    } else {
      // Events: owner opens the editor; a shared (non-owner) viewer gets read-only.
      setEventModal({ open: true, initial: item, date: item.date, readOnly: item.is_owner === false });
    }
  };
  const onEditGoal = (g) => setGoalModal({ open: true, initial: g });
  const onOpenDate = (date) => { setView('daily'); setAnchor(date); };
  const onOpenWeek = (weekStartDate) => { setView('weekly'); setAnchor(weekStartDate); };

  // Weekly drag-and-drop: move a task to another day of the week → change its
  // due date to that day while PRESERVING the time-of-day. We shift the stored
  // UTC ``due_at`` by the whole-day delta between the card's current local
  // due date and the drop target, which keeps the local clock time intact
  // regardless of timezone. The UI is updated optimistically, then reconciled
  // against a fresh feed load.
  const onMoveTask = useCallback(async (task, newDate) => {
    if (!task?.id || !task.due_at || !task.due_date || !newDate) return;
    if (task.due_date === newDate) return;
    const [oy, om, od] = task.due_date.split('-').map(Number);
    const [ny, nm, nd] = newDate.split('-').map(Number);
    const diffDays = Math.round((Date.UTC(ny, nm - 1, nd) - Date.UTC(oy, om - 1, od)) / 86400000);
    if (!diffDays) return;
    const newDueAt = new Date(Date.parse(task.due_at) + diffDays * 86400000).toISOString();

    // Optimistic: pull the task out of every day column and drop it on target.
    setData((d) => {
      if (!d?.days) return d;
      const moved = { ...task, due_at: newDueAt, due_date: newDate, carried_over: false };
      const nextDays = d.days.map((day) => {
        const kept = (day.tasks || []).filter((t) => t.id !== task.id);
        if (day.date === newDate) kept.push(moved);
        return { ...day, tasks: kept };
      });
      return { ...d, days: nextDays };
    });

    try {
      await notesApi.patch(`/notes/tasks/tasks/${task.id}`, { due_at: newDueAt });
      toast.success(`Moved “${task.title}” to ${prettyDateLong(newDate)}`);
      load({ silent: true });
    } catch (e) {
      toast.error('Could not move task');
      load({ silent: true });
    }
  }, [load]);

  const onSaveDayNote = useCallback(async (date, content) => {
    await notesApi.put('/planner/day-note', { date, content });
  }, []);

  // ---- Full-parity task-card actions (soft-refresh, no skeleton flash) -----
  const onToggleComplete = useCallback(async (t) => {
    try {
      await notesApi.post(`/notes/tasks/tasks/${t.id}/toggle-complete`);
      load({ silent: true });
    } catch { toast.error('Could not update task'); }
  }, [load]);
  const quickSetDue = useCallback(async (t, iso) => {
    try {
      await notesApi.patch(`/notes/tasks/tasks/${t.id}`, { due_at: iso });
      toast.success(iso ? 'Due date updated' : 'Due date cleared');
      load({ silent: true });
    } catch { toast.error('Could not update due date'); }
  }, [load]);
  const quickSetPriority = useCallback(async (t, p) => {
    try {
      await notesApi.patch(`/notes/tasks/tasks/${t.id}`, { priority: p });
      toast.success('Priority updated');
      load({ silent: true });
    } catch { toast.error('Could not update priority'); }
  }, [load]);
  const quickComment = useCallback(async (t, body) => {
    await notesApi.post(`/notes/tasks/tasks/${t.id}/comments`, { body, mentions: [], invite_user_ids: [] });
    toast.success('Comment added');
    load({ silent: true });
  }, [load]);
  const quickPingStatus = useCallback(async (t) => {
    const { data } = await notesApi.get(`/notes/tasks/tasks/${t.id}/pings`);
    return data;
  }, []);
  const quickPing = useCallback(async (t, userId) => {
    const { data } = await notesApi.post(`/notes/tasks/tasks/${t.id}/ping`, { user_id: userId });
    toast.success('Ping sent');
    return data;
  }, []);
  const syncTaskFromSubtasks = useCallback(() => { load({ silent: true }); }, [load]);

  // Overlay openers passed down to the task cards.
  const onTaskQuickActions = useCallback((pos, task) => setQuickPanel({ task, x: pos.x, y: pos.y }), []);
  const onOpenSubtasks = useCallback((anchor, task, canEdit) => setSubtaskPopover({ id: task.id, anchor, canEdit }), []);
  const onOpenTaskCard = useCallback((t) => onOpenTask(t.id), [navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const afterSave = useCallback(() => { load({ silent: true }); loadDirs(); }, [load, loadDirs]);
  const newEvent = (date, title) => setEventModal({ open: true, initial: title ? { title } : null, date: date || anchor });
  const newMeeting = (date, title) => setMeetingModal({ open: true, initial: title ? { title } : null, date: date || anchor });
  const newGoal = (title) => setGoalModal({ open: true, initial: title ? { title } : null });

  // Command-palette quick-create: open the dialog with any parsed fields
  // prefilled (title / date / time / — for meetings — resolved attendees). Time
  // is left empty when none was parsed so the user must set it (or pick all-day).
  const openEventPrefill = useCallback((p = {}) => {
    const initial = {};
    if (p.title) initial.title = p.title;
    if (p.start_time) initial.start_time = p.start_time;
    if (p.end_time) initial.end_time = p.end_time;
    setEventModal({ open: true, initial: Object.keys(initial).length ? initial : null, date: p.date || anchor });
  }, [anchor]);
  const openMeetingPrefill = useCallback((p = {}) => {
    const initial = {};
    if (p.title) initial.title = p.title;
    if (p.start_time) initial.start_time = p.start_time;
    if (p.end_time) initial.end_time = p.end_time;
    if (p.attendeeIds && p.attendeeIds.length) initial.attendees = p.attendeeIds.map((id) => ({ id }));
    setMeetingModal({ open: true, initial: Object.keys(initial).length ? initial : null, date: p.date || anchor });
  }, [anchor]);

  // Soft refresh used by the auto-refresh timer + manual refresh button. Pulls a
  // fresh feed with no skeleton flash and toasts any new shared activity.
  const softRefreshWithDigest = useCallback(() => { load({ silent: true, digest: true }); }, [load]);
  const anyOverlayOpen = eventModal.open || meetingModal.open || goalModal.open
    || !!quickPanel || !!subtaskPopover || jumpOpen || paletteOpen;

  const segCls = (active) => `n-focus inline-flex items-center gap-1.5 rounded-[9px] px-3 py-1.5 text-[13px] font-semibold transition-colors ${
    active ? 'bg-[var(--n-accent-bg)] text-[var(--n-ink)] shadow-[inset_0_0_0_1px_var(--n-accent)]' : 'text-[var(--n-muted)] hover:text-[var(--n-ink)]'
  }`;

  return (
    <div className={`notes-scope ${isDark ? 'dark' : ''} flex h-screen w-full flex-col overflow-hidden`} style={{ background: 'var(--n-canvas)', paddingRight: chatReserve || undefined, transition: 'padding-right 160ms ease' }}>
      {/* Top chrome */}
      <header className="flex h-14 shrink-0 items-center gap-2 border-b border-[var(--n-line)] bg-[var(--n-surface)] px-3 sm:px-4">
        <div className="flex items-center gap-2">
          <CalendarRange size={20} className="text-[var(--n-accent)]" />
          <span className="font-display text-xl tracking-wide text-[var(--n-ink)] hidden sm:inline">Planner</span>
        </div>
        <div className="ml-1 sm:ml-3"><ModuleSwitch /></div>
        <div className="ml-auto flex items-center gap-1.5">
          {chatEnabled && chatReserve === 0 && (
            <button onClick={() => setChatOpen(true)} title="Chat" aria-label="Chat" data-testid="planner-chat-toggle"
              className="relative n-focus inline-flex h-9 w-9 items-center justify-center rounded-lg text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)]">
              <MessageSquare size={18} />
              {chatUnread > 0 && (
                <span data-testid="planner-chat-unread-badge" className="absolute -right-0.5 -top-0.5 grid h-4 min-w-4 place-items-center rounded-full px-1 text-[9px] font-bold text-white" style={{ background: 'var(--n-danger)' }}>{chatUnread > 99 ? '99+' : chatUnread}</span>
              )}
            </button>
          )}
          <button onClick={toggleTheme} title="Toggle theme" data-testid="planner-theme-toggle"
            className="n-focus inline-flex h-9 w-9 items-center justify-center rounded-lg text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)]">{isDark ? <Sun size={17} /> : <Moon size={17} />}</button>
          <div className="ml-1 hidden h-8 w-8 place-items-center rounded-full bg-[var(--n-accent)] text-[13px] font-bold text-white sm:grid" title={principal?.name}>{(principal?.name || 'U').charAt(0).toUpperCase()}</div>
          <button onClick={logout} title="Sign out" data-testid="planner-logout"
            className="n-focus inline-flex h-9 w-9 items-center justify-center rounded-lg text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)]"><LogOut size={17} /></button>
        </div>
      </header>

      {/* Planner sub-header */}
      <div className="sticky top-0 z-20 border-b border-[var(--n-line)] bg-[color:var(--n-canvas)]/85 backdrop-blur">
        <div className="mx-auto flex max-w-6xl flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <div className="flex items-center rounded-lg border border-[var(--n-border)] bg-[var(--n-surface)] p-0.5">
              <button onClick={() => step(-1)} data-testid="planner-date-prev-button" aria-label={`Previous ${view}`} title={`Previous ${view} (←)`}
                className="n-focus inline-flex h-8 w-8 items-center justify-center rounded-md text-[var(--n-ink-soft)] transition-colors hover:bg-[var(--n-accent-bg)] active:scale-95"><ChevronLeft size={17} /></button>
              <button onClick={() => step(1)} data-testid="planner-date-next-button" aria-label={`Next ${view}`} title={`Next ${view} (→)`}
                className="n-focus inline-flex h-8 w-8 items-center justify-center rounded-md text-[var(--n-ink-soft)] transition-colors hover:bg-[var(--n-accent-bg)] active:scale-95"><ChevronRight size={17} /></button>
            </div>
            <Popover open={jumpOpen} onOpenChange={setJumpOpen}>
              <PopoverTrigger asChild>
                <button type="button" data-testid="planner-jump-trigger" title="Jump to date (G)" aria-label="Jump to date"
                  className="n-focus min-w-0 rounded-lg px-1.5 py-0.5 text-left transition-colors hover:bg-[var(--n-accent-bg)]">
                  <div className="flex items-center gap-1.5">
                    <span className="truncate font-display text-xl leading-tight text-[color:var(--p-text)] sm:text-2xl" data-testid="planner-headline">{headline}</span>
                    <ChevronDown size={16} className="shrink-0 text-[var(--n-muted)]" />
                  </div>
                  <div className="text-[11px] uppercase tracking-wider text-[color:var(--p-muted)]">{sub}</div>
                </button>
              </PopoverTrigger>
              <PopoverContent align="start" sideOffset={8} className="notes-scope w-auto border-[var(--n-border)] bg-[var(--n-surface)] p-0" data-testid="planner-jump-popover">
                <Calendar
                  mode="single"
                  weekStartsOn={0}
                  selected={parseISO(anchor)}
                  defaultMonth={parseISO(anchor)}
                  onSelect={(d) => { if (d) { setAnchor(fmtISO(d)); setJumpOpen(false); } }}
                />
                <div className="border-t border-[var(--n-border)] px-3 py-2">
                  <button type="button" data-testid="planner-jump-today"
                    onClick={() => { setAnchor(todayStr()); setJumpOpen(false); }}
                    className="n-focus w-full rounded-md bg-[var(--n-accent)] px-2.5 py-1.5 text-[12.5px] font-semibold text-white transition-colors hover:bg-[var(--n-accent-2)]">Go to today</button>
                </div>
              </PopoverContent>
            </Popover>
            <button onClick={goToday} data-testid="planner-today-button"
              className={`n-focus ml-1 rounded-lg px-2.5 py-1.5 text-[12.5px] font-semibold transition-colors ${isOnToday ? 'border border-[var(--n-border)] text-[var(--n-muted)]' : 'bg-[var(--n-accent)] text-white hover:bg-[var(--n-accent-2)]'}`}>Today</button>
          </div>

          <div className="flex items-center gap-2">
            <PlannerAutoRefresh paused={anyOverlayOpen} onRefresh={softRefreshWithDigest} />
            <button type="button" onClick={() => setPaletteOpen(true)} data-testid="planner-command-trigger"
              title="Search & commands (Ctrl / Cmd + K)" aria-label="Open command palette"
              className="n-focus inline-flex items-center gap-2 rounded-lg border border-[var(--n-border)] bg-[var(--n-surface)] px-2.5 py-2 text-[12.5px] text-[var(--n-muted)] transition-colors hover:border-[var(--n-accent)] hover:text-[var(--n-ink)]">
              <Search size={15} />
              <span className="hidden items-center gap-1 lg:inline-flex">Search <kbd className="rounded border border-[var(--n-border)] bg-[var(--n-accent-bg)] px-1 py-0.5 text-[10px] font-semibold">Ctrl K</kbd></span>
            </button>
            <div data-testid="planner-view-tabs" className="inline-flex items-center rounded-xl border border-[var(--n-border)] bg-[var(--n-surface)] p-0.5">
              {VIEWS.map((v) => (
                <button key={v.key} data-testid={`planner-view-${v.key}`} onClick={() => setView(v.key)} className={segCls(view === v.key)}>
                  <v.icon size={15} /> <span className="hidden sm:inline">{v.label}</span>
                </button>
              ))}
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="planner-create-button"
                  className="n-focus inline-flex items-center gap-1.5 rounded-lg bg-[var(--n-accent)] px-3 py-2 text-[13px] font-semibold text-white transition-colors hover:bg-[var(--n-accent-2)] active:scale-[0.98]"><Plus size={16} /> <span className="hidden sm:inline">Create</span></button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="notes-scope w-48 border-[var(--n-border)] bg-[var(--n-surface)] text-[var(--n-ink)]">
                <DropdownMenuItem onClick={() => newEvent()} data-testid="planner-create-event" className="cursor-pointer gap-2 text-[13px]"><CalendarPlus size={15} className="text-[var(--n-accent)]" /> New event</DropdownMenuItem>
                <DropdownMenuItem onClick={() => newMeeting()} data-testid="planner-create-meeting" className="cursor-pointer gap-2 text-[13px]"><Users size={15} className="text-[var(--n-accent)]" /> New meeting</DropdownMenuItem>
                <DropdownMenuItem onClick={() => newGoal()} data-testid="planner-create-goal" className="cursor-pointer gap-2 text-[13px]"><Target size={15} className="text-[var(--n-accent)]" /> New goal</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="min-h-0 flex-1 overflow-y-auto n-scroll px-4 py-5 sm:px-6 lg:px-8" onTouchStart={onBodyTouchStart} onTouchEnd={onBodyTouchEnd}>
        {loading || !data ? (
          <div className="mx-auto max-w-6xl" data-testid="planner-loading">
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_360px]">
              <Skeleton className="h-[520px] w-full rounded-[var(--p-radius-lg)] bg-[color:var(--p-card-2)]" />
              <div className="space-y-4">
                <Skeleton className="h-48 w-full rounded-[var(--p-radius-lg)] bg-[color:var(--p-card-2)]" />
                <Skeleton className="h-48 w-full rounded-[var(--p-radius-lg)] bg-[color:var(--p-card-2)]" />
              </div>
            </div>
          </div>
        ) : view === 'daily' ? (
          <DailyPlannerView data={data} onToggleGoal={onToggleGoal} onQuantityGoal={onQuantityGoal} onOpenTask={onOpenTask} onOpenNote={onOpenNote}
            onOpenItem={onOpenItem} onEditGoal={onEditGoal} onNewEvent={newEvent} onNewGoal={newGoal} onSaveDayNote={onSaveDayNote} onOpenDate={onOpenDate}
            cardHandlers={{ onOpen: onOpenTaskCard, onQuickActions: onTaskQuickActions, onOpenSubtasks }} />
        ) : view === 'weekly' ? (
          <WeeklyPlannerView data={data} onToggleGoal={onToggleGoal} onQuantityGoal={onQuantityGoal} onOpenTask={onOpenTask} onOpenNote={onOpenNote}
            onOpenItem={onOpenItem} onEditGoal={onEditGoal} onNewEvent={newEvent} onOpenDate={onOpenDate} onOpenWeek={onOpenWeek} onMoveTask={onMoveTask}
            cardHandlers={{ onOpen: onOpenTaskCard, onQuickActions: onTaskQuickActions, onOpenSubtasks }} />
        ) : (
          <MonthlyPlannerView data={data} onOpenDate={onOpenDate} onOpenWeek={onOpenWeek} onToggleGoal={onToggleGoal} onQuantityGoal={onQuantityGoal} onEditGoal={onEditGoal} />
        )}
      </div>

      {/* Modals */}
      <EventDialog open={eventModal.open} onOpenChange={(o) => setEventModal((m) => ({ ...m, open: o }))}
        initial={eventModal.initial} defaultDate={eventModal.date} onSaved={afterSave} taskDirectory={taskDirectory} directory={directory} readOnly={!!eventModal.readOnly} />
      <MeetingDialog open={meetingModal.open} onOpenChange={(o) => setMeetingModal((m) => ({ ...m, open: o }))}
        initial={meetingModal.initial} defaultDate={meetingModal.date} directory={directory} onSaved={afterSave} taskDirectory={taskDirectory} />
      <GoalDialog open={goalModal.open} onOpenChange={(o) => setGoalModal((m) => ({ ...m, open: o }))}
        initial={goalModal.initial} onSaved={afterSave} directory={directory} />

      {/* Command palette (Ctrl / Cmd + K) */}
      <PlannerCommandPalette
        open={paletteOpen}
        onOpenChange={setPaletteOpen}
        onPickDate={onOpenDate}
        onOpenNote={onOpenNote}
        onOpenTask={onOpenTask}
        onOpenItem={onOpenItem}
        onEditGoal={onEditGoal}
        onCreateEvent={openEventPrefill}
        onCreateMeeting={openMeetingPrefill}
        onCreateGoal={(p) => newGoal(p && p.title)}
        directory={directory}
      />

      {/* Full-parity task-card overlays (subtask popover + quick actions) */}
      {subtaskPopover && (
        <SmartPopover anchor={subtaskPopover.anchor} width={288}
          onClose={() => setSubtaskPopover(null)}
          testId="planner-subtask-popover" backdropTestId="planner-subtask-popover-backdrop"
          zPanel="z-[321]" zBackdrop="z-[320]">
          <div className="p-2.5">
            <div className="mb-1.5 flex items-center justify-between px-0.5">
              <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--n-muted)]"><ListChecks size={12} /> Sub-tasks</span>
              <button type="button" onClick={() => setSubtaskPopover(null)} data-testid="planner-subtask-popover-close"
                className="n-focus text-[var(--n-muted-2)] hover:text-[var(--n-ink)]"><X size={13} /></button>
            </div>
            <TaskSubtasks compact allowAdd taskId={subtaskPopover.id} canEdit={subtaskPopover.canEdit} onTaskSync={syncTaskFromSubtasks} />
          </div>
        </SmartPopover>
      )}
      {quickPanel && (
        <TaskQuickActions
          task={quickPanel.task}
          position={{ x: quickPanel.x, y: quickPanel.y }}
          onClose={() => setQuickPanel(null)}
          onToggleComplete={onToggleComplete}
          onSetDue={quickSetDue}
          onSetPriority={quickSetPriority}
          onPostComment={quickComment}
          onFetchPingStatus={quickPingStatus}
          onPing={quickPing} />
      )}

      {chatEnabled && <ChatDock open={chatOpen} onClose={() => setChatOpen(false)} onUnread={setChatUnread} onReserveChange={setChatReserve} />}
      <PlannerReminderAlerts />
      <PlannerNavShortcuts />
    </div>
  );
}
